import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { colors } from "../../../../theme";
import { ZONE_RISK_LEVELS, zoneRiskLabel } from "../../status/status-components/zoneUtils";
import WarningDialog from "../../../../components/WarningDialog";

const EMPTY_FORM = { name: "", note: "", risk: "none", target_sl: "", purdue_level: "", subnets: "", vlans: "" };

// 0-4/0-5, come i campi lato backend (zone.mjs) -- vuoto = non impostato,
// distinto da 0 (che è un livello valido, non "nessuno").
const TARGET_SL_OPTIONS = [0, 1, 2, 3, 4];
const PURDUE_LEVEL_OPTIONS = [5, 4, 3, 2, 1, 0];

const SectionLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: 11,
      fontWeight: 700,
      color: "#459095",
      textTransform: "uppercase",
      letterSpacing: 0.8,
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

// Form di creazione/modifica per UNA zona alla volta -- estratto da
// ZoneManagerDialog (che elencava tutte le zone in una griglia di card),
// ora aperto direttamente dal grafo Network Zones: "+" per riga/livello
// Purdue (creazione, con purdue_level precompilato) oppure dal pannello
// Inspector di una zona selezionata (modifica/eliminazione).
const ZoneFormDialog = ({
  open,
  onClose,
  zone, // null = create, oggetto zona = edit
  initialPurdueLevel,
  zones,
  onCreate,
  onUpdate,
  onDelete,
  // Opzionali: quando passati, mostra la mini-matrice "conduit con le altre
  // zone" (solo in modalità edit, cioè quando `zone` è impostato).
  conduitRules,
  onCreateRule,
  onUpdateRule,
  onDeleteRule,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const editingId = zone ? (zone._id ?? zone.id) : null;

  const sortedOtherZones = useMemo(
    () => [...(zones || [])]
      .filter((z) => (z._id ?? z.id) !== editingId)
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
    [zones, editingId]
  );

  useEffect(() => {
    if (!open) return;
    if (zone) {
      setForm({
        name: zone.name ?? "",
        note: zone.note ?? "",
        risk: zone.risk ?? "none",
        target_sl: zone.target_sl === null || zone.target_sl === undefined ? "" : zone.target_sl,
        purdue_level: zone.purdue_level === null || zone.purdue_level === undefined ? "" : zone.purdue_level,
        subnets: (zone.subnets || []).join(", "),
        vlans: (zone.vlans || []).join(", "),
      });
    } else {
      setForm({ ...EMPTY_FORM, purdue_level: initialPurdueLevel === undefined || initialPurdueLevel === null ? "" : initialPurdueLevel });
    }
  }, [open, zone, initialPurdueLevel]);

  const handleSubmit = () => {
    if (!form.name.trim()) return;
    const payload = {
      ...form,
      subnets: form.subnets.split(/[,\n]/).map((s) => s.trim()).filter(Boolean),
      vlans: form.vlans.split(",").map((v) => v.trim()).filter(Boolean).map(Number).filter((v) => Number.isInteger(v)),
    };
    if (editingId) onUpdate(editingId, payload);
    else onCreate(payload);
    onClose();
  };

  const handleDelete = () => {
    if (!editingId) return;
    setDeleteConfirmOpen(true);
  };

  // Matrice "conduit con le altre zone": una riga per ogni altra zona,
  // regola generica (qualsiasi servizio, service=""). Direzione inclusa
  // (both/a_to_b/b_to_a) per un allow-list mirata senza dover aprire "Manage
  // conduit rules" -- "not allowed" resta un singolo stato non direzionale,
  // solo gli stati "allowed" si dividono per direzione.
  const findGenericRule = (nameA, nameB) => (conduitRules || []).find((r) =>
    !r.service && ((r.zone_a === nameA && r.zone_b === nameB) || (r.zone_a === nameB && r.zone_b === nameA))
  );

  // Una regola trovata da findGenericRule può avere zone_a/zone_b nell'ordine
  // opposto rispetto a "questa zona -> otherName" (es. creata editando
  // l'altra zona) -- la direzione va riletta rispetto a quell'ordine reale,
  // non assunta sempre "questa zona = from".
  const matrixValueFor = (otherName) => {
    const rule = findGenericRule(form.name, otherName);
    if (!rule) return "unset";
    if (!rule.allowed) return "denied";
    const dir = rule.direction || "both";
    if (dir === "both") return "both";
    const reversed = rule.zone_a === otherName; // stored as otherName -> this zone
    if (dir === "a_to_b") return reversed ? "b_to_a" : "a_to_b";
    return reversed ? "a_to_b" : "b_to_a"; // dir === "b_to_a"
  };

  const handleMatrixChange = (otherName, value) => {
    const existing = findGenericRule(form.name, otherName);
    if (value === "unset") {
      if (existing) onDeleteRule(existing._id ?? existing.id);
      return;
    }
    const allowed = value !== "denied";
    const direction = value === "denied" ? "both" : value;
    // Riscrive sempre zone_a/zone_b come "questa zona -> otherName": così la
    // direzione scelta qui ha sempre lo stesso significato, indipendentemente
    // da come la regola era stata salvata in precedenza.
    const payload = { zone_a: form.name, zone_b: otherName, service: "", allowed, direction, note: existing?.note || "" };
    if (existing) onUpdateRule(existing._id ?? existing.id, payload);
    else onCreateRule(payload);
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: "#161616", backgroundImage: "none" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, borderBottom: `1px solid ${colors.grey[800]}`, pb: 1.5 }}>
        <PlaceIcon sx={{ color: "#459095", fontSize: 22 }} />
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: colors.grey[100] }}>
          {editingId ? "Edit Zone" : "New Zone"}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: "#161616", pt: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <TextField
            label="Zone name"
            size="small"
            fullWidth
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <TextField
            label="Note"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Risk</InputLabel>
            <Select
              label="Risk"
              value={form.risk}
              onChange={(e) => setForm((f) => ({ ...f, risk: e.target.value }))}
            >
              {ZONE_RISK_LEVELS.map((r) => (
                <MenuItem key={r} value={r}>{zoneRiskLabel(r)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Target Security Level (IEC 62443, optional)</InputLabel>
            <Select
              label="Target Security Level (IEC 62443, optional)"
              value={form.target_sl}
              onChange={(e) => setForm((f) => ({ ...f, target_sl: e.target.value }))}
            >
              <MenuItem value="">Not set</MenuItem>
              {TARGET_SL_OPTIONS.map((sl) => (
                <MenuItem key={sl} value={sl}>SL {sl}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Purdue Level (estimated, optional)</InputLabel>
            <Select
              label="Purdue Level (estimated, optional)"
              value={form.purdue_level}
              onChange={(e) => setForm((f) => ({ ...f, purdue_level: e.target.value }))}
            >
              <MenuItem value="">Not set</MenuItem>
              {PURDUE_LEVEL_OPTIONS.map((lvl) => (
                <MenuItem key={lvl} value={lvl}>Level {lvl}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Subnets / IP addresses (comma-separated, optional)"
            placeholder="10.0.1.0/24, 10.0.2.15"
            helperText="Which zone this is on the network — a CIDR subnet or a single IP (sent to Orion as-is, /32 for a single IP)."
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={form.subnets}
            onChange={(e) => setForm((f) => ({ ...f, subnets: e.target.value }))}
          />
          <TextField
            label="VLANs (comma-separated, optional)"
            placeholder="10, 20"
            size="small"
            fullWidth
            value={form.vlans}
            onChange={(e) => setForm((f) => ({ ...f, vlans: e.target.value }))}
          />
        </Box>

        {conduitRules && editingId && (
          <Box sx={{ mt: 2.5 }}>
            <Divider sx={{ mb: 2.5, borderColor: `${colors.grey[700]}55` }} />
            <SectionLabel>Conduits with other zones</SectionLabel>
            <Typography sx={{ fontSize: 11, color: colors.grey[500], mb: 1.2 }}>
              Quick allow/deny between "{form.name || "this zone"}" and every other zone, any service, both directions.
              For a specific service or direction, use "Manage conduit rules" instead.
            </Typography>
            {sortedOtherZones.length === 0 ? (
              <Typography sx={{ color: colors.grey[500], fontSize: 12.5, fontStyle: "italic" }}>
                No other zones yet.
              </Typography>
            ) : (
              <Box sx={{
                display: "flex", flexDirection: "column", gap: 0.8,
                maxHeight: 240, overflowY: "auto", pr: 0.5,
              }}>
                {sortedOtherZones.map((oz) => {
                  const value = matrixValueFor(oz.name);
                  return (
                    <Box key={oz._id ?? oz.id} sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography sx={{ fontSize: 12.5, color: colors.grey[200], flex: 1, minWidth: 90 }} noWrap>
                        {oz.name}
                      </Typography>
                      <ToggleButtonGroup
                        size="small" exclusive value={value}
                        onChange={(e, v) => v && handleMatrixChange(oz.name, v)}
                        sx={{
                          "& .MuiToggleButton-root": { textTransform: "none", fontSize: 11, px: 0.8, py: 0.3, color: colors.grey[400], borderColor: colors.grey[800] },
                          "& .MuiToggleButton-root.Mui-selected": { color: colors.grey[100], bgcolor: `${colors.grey[700]}55` },
                        }}
                      >
                        <ToggleButton value="unset">Not set</ToggleButton>
                        <ToggleButton value="denied" sx={{ "&.Mui-selected": { color: "#ff4444 !important", bgcolor: "#ff444422 !important" } }}>Not allowed</ToggleButton>
                        <ToggleButton value="both" sx={{ "&.Mui-selected": { color: "#4cceac !important", bgcolor: "#4cceac22 !important" } }}>
                          <SyncAltIcon sx={{ fontSize: 13, mr: 0.4 }} />Both
                        </ToggleButton>
                        <ToggleButton value="a_to_b" sx={{ "&.Mui-selected": { color: "#4cceac !important", bgcolor: "#4cceac22 !important" } }}>
                          <ArrowForwardIcon sx={{ fontSize: 13, mr: 0.4 }} />A→B
                        </ToggleButton>
                        <ToggleButton value="b_to_a" sx={{ "&.Mui-selected": { color: "#4cceac !important", bgcolor: "#4cceac22 !important" } }}>
                          <ArrowBackIcon sx={{ fontSize: 13, mr: 0.4 }} />B→A
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#161616", borderTop: `1px solid ${colors.grey[800]}`, justifyContent: "space-between" }}>
        <Box>
          {editingId && <Button color="error" onClick={handleDelete}>Delete</Button>}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" disableElevation disabled={!form.name.trim()} onClick={handleSubmit}>
            {editingId ? "Save" : "Create Zone"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
    <WarningDialog
      open={deleteConfirmOpen}
      setOpen={setDeleteConfirmOpen}
      setCheck={() => {
        setDeleteConfirmOpen(false);
        onDelete(editingId);
        onClose();
      }}
      message={`Zone "${zone?.name ?? ""}" will be permanently deleted. This cannot be undone.`}
    />
    </>
  );
};

export default ZoneFormDialog;
